/**
 * Page Navigation
 * Handles page navigation UI and branding navigation
 */

export class PageNavigation {
  constructor(pageManager, translator = null) {
    this.pageManager = pageManager;
    this.translator = translator;
  }

  /**
   * Setup page navigation buttons
   */
  setupNavigationButtons() {
    const pages = document.querySelectorAll('.page');
    
    if (pages.length <= 1) {
      return;
    }

    pages.forEach((page, index) => {
      const pageNum = parseInt(page.getAttribute('data-num')) || (index + 1);
      const totalPages = pages.length;
      
      let navigationHTML = '<div class="form-group page-navigation-container">';
      let hasButtons = false;

      const t = this.translator?.t.bind(this.translator) || ((key) => key);
      
      // Previous button (not on first page)
      if (pageNum > 1) {
        navigationHTML += `<a href="#" data-to="${pageNum - 1}" class="btn btn-secondary prev_page">${t('buttons.previous_page')}</a>`;
        hasButtons = true;
      } else {
        // Add empty div to maintain layout when only next button exists
        navigationHTML += '<div class="prev_page_placeholder"></div>';
      }

      // Next button (not on last page)
      if (pageNum < totalPages) {
        navigationHTML += `<button type="button" data-to="${pageNum + 1}" class="btn btn-primary next_page">${t('buttons.next_page')}</button>`;
        hasButtons = true;
      } else {
        // Add empty div to maintain layout when only prev button exists
        navigationHTML += '<div class="next_page_placeholder"></div>';
      }

      if (hasButtons) {
        navigationHTML += '</div>';
        page.insertAdjacentHTML('beforeend', navigationHTML);
      }
    });

    // Setup event listeners
    this.setupButtonListeners();
  }

  /**
   * Setup button event listeners
   */
  setupButtonListeners() {
    // Next page buttons
    const nextButtons = document.querySelectorAll('.next_page');
    nextButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleNextPage();
      });
    });

    // Previous page buttons
    const prevButtons = document.querySelectorAll('.prev_page');
    prevButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.handlePreviousPage();
      });
    });
  }

  /**
   * Handle next page click
   */
  handleNextPage() {
    if (this.pageManager.nextPage()) {
      // Navigation successful
      this.updateBrandingNavigation();
    } else {
      // Validation failed - focus on first invalid field
      const currentPage = this.pageManager.getCurrentPage();
      const page = document.querySelector(`.page[data-num="${currentPage}"]`);
      if (page) {
        const firstInvalid = page.querySelector('.error-container.validation-error-message:not(:empty)');
        if (firstInvalid) {
          const fieldId = firstInvalid.getAttribute('data-field-id');
          const field = document.querySelector(`[name="${fieldId}"], [id="${fieldId}"]`);
          if (field) {
            field.scrollIntoView({ behavior: 'smooth', block: 'center' });
            field.focus();
          }
        }
      }
    }
  }

  /**
   * Handle previous page click
   */
  handlePreviousPage() {
    this.pageManager.previousPage();
    this.updateBrandingNavigation();
  }

  /**
   * Setup branding page navigation
   * @param {Array} pages - Pages array
   */
  setupBrandingNavigation(pages) {
    const brandingNavContainer = document.getElementById('branding-page-navigation');
    if (!brandingNavContainer) return;

    if (!pages || pages.length <= 1) {
      brandingNavContainer.style.display = 'none';
      return;
    }

    // Clear existing content
    brandingNavContainer.innerHTML = '';

    // Create page list container
    const pageListContainer = document.createElement('div');
    pageListContainer.className = 'branding-page-list';

    // Sort pages by position
    const sortedPages = [...pages].sort((a, b) => {
      const posA = a.poisition || a.position || 0;
      const posB = b.poisition || b.position || 0;
      return posA - posB;
    });

    // Create page items
    sortedPages.forEach(page => {
      const pagePosition = page.poisition || page.position || 1;
      const pageItem = document.createElement('div');
      pageItem.className = 'branding-page-item';
      pageItem.setAttribute('data-page-num', pagePosition);
      pageItem.setAttribute('data-page-id', page.id);
      
      const displayText = page.title || page.id;
      pageItem.textContent = displayText;
      pageItem.style.cursor = 'pointer';

      pageItem.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleBrandingPageClick(pagePosition);
      });

      pageListContainer.appendChild(pageItem);
    });

    brandingNavContainer.appendChild(pageListContainer);
    brandingNavContainer.style.display = 'block';

    // Add scrolling if more than 4 pages
    if (sortedPages.length > 4) {
      pageListContainer.classList.add('scrollable');
    }

    // Initial highlight
    this.updateBrandingNavigation();
  }

  /**
   * Handle branding page click
   * @param {number} targetPageNum - Target page number
   */
  handleBrandingPageClick(targetPageNum) {
    const currentPage = this.pageManager.getCurrentPage();

    if (targetPageNum === currentPage) {
      return;
    }

    // If navigating backward, allow without validation
    if (targetPageNum < currentPage) {
      this.pageManager.navigateToPage(targetPageNum, false);
      this.updateBrandingNavigation();
      return;
    }

    // If navigating forward, validate first
    if (this.pageManager.validateCurrentPage()) {
      this.pageManager.navigateToPage(targetPageNum, false);
      this.updateBrandingNavigation();
    }
  }

  /**
   * Update branding navigation highlighting
   */
  updateBrandingNavigation() {
    const brandingNavContainer = document.getElementById('branding-page-navigation');
    if (!brandingNavContainer) return;

    const currentPage = this.pageManager.getCurrentPage();
    const allPageItems = brandingNavContainer.querySelectorAll('.branding-page-item');

    allPageItems.forEach(item => {
      item.classList.remove('active');
    });

    const currentPageItem = brandingNavContainer.querySelector(`.branding-page-item[data-page-num="${currentPage}"]`);
    if (currentPageItem) {
      currentPageItem.classList.add('active');
    }
  }
}

